import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { XMarkIcon, CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

import { cn } from '../../utils/cn';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastData {
  id: string;
  title: string;
  description?: string;
  type: ToastType;
  duration?: number;
}

interface ToastFunction {
  (data: Omit<ToastData, 'id'>): void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

interface ToastContextValue {
  toast: ToastFunction;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const icons = {
  success: CheckCircleIcon,
  error: ExclamationCircleIcon,
  warning: ExclamationCircleIcon,
  info: InformationCircleIcon,
};

const styles = {
  success: 'border-l-4 border-l-mv-green-700',
  error: 'border-l-4 border-l-mv-error-500',
  warning: 'border-l-4 border-l-mv-warning-500',
  info: 'border-l-4 border-l-mv-blue-600',
};

const iconStyles = {
  success: 'text-mv-green-700',
  error: 'text-mv-error-500',
  warning: 'text-mv-warning-500',
  info: 'text-mv-blue-600',
};

export function Toast({ data, onClose }: { data: ToastData; onClose: () => void }) {
  const Icon = icons[data.type];

  return (
    <ToastPrimitive.Root
      className={cn(
        'grid grid-cols-[auto_1fr_auto] items-start gap-3 rounded-lg bg-white p-4 shadow-dropdown',
        'data-[state=open]:animate-slide-up data-[state=closed]:animate-fade-out',
        styles[data.type]
      )}
      duration={data.duration ?? 5000}
      onOpenChange={(open) => !open && onClose()}
    >
      <Icon className={cn('h-5 w-5', iconStyles[data.type])} />
      <div className="grid gap-1">
        <ToastPrimitive.Title className="text-sm font-medium text-mv-gray-900">
          {data.title}
        </ToastPrimitive.Title>
        {data.description && (
          <ToastPrimitive.Description className="text-sm text-mv-gray-500">
            {data.description}
          </ToastPrimitive.Description>
        )}
      </div>
      <ToastPrimitive.Close asChild>
        <button
          // GAP-015: text-mv-gray-500 for WCAG AA contrast
          className="rounded p-1 text-mv-gray-500 hover:bg-mv-gray-100 hover:text-mv-gray-700"
          aria-label="Close notification"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}

export function Toaster({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback((data: Omit<ToastData, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { ...data, id }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const successFn = (title: string, description?: string) => addToast({ type: 'success', title, description });
  const errorFn = (title: string, description?: string) => addToast({ type: 'error', title, description });
  const warningFn = (title: string, description?: string) => addToast({ type: 'warning', title, description });
  const infoFn = (title: string, description?: string) => addToast({ type: 'info', title, description });

  // Create toast function with methods attached
  const toastFn = Object.assign(addToast, {
    success: successFn,
    error: errorFn,
    warning: warningFn,
    info: infoFn,
  }) as ToastFunction;

  const value: ToastContextValue = {
    toast: toastFn,
    success: successFn,
    error: errorFn,
    warning: warningFn,
    info: infoFn,
  };

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider>
        {children}
        <ToastPrimitive.Viewport
          className="fixed bottom-4 right-4 z-50 flex max-w-sm flex-col gap-2"
          aria-live="polite"
          aria-label="Notifications"
        />
        {toasts.map((toast) => (
          <Toast key={toast.id} data={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a Toaster');
  }
  return context;
}
